import * as React from "react";
import { cva } from "class-variance-authority";
import {
  AlertCircle,
  CheckCircle2,
  Info,
  X,
  AlertTriangle,
} from "lucide-react";

import { cn } from "../lib/utils";
import {
  ALERT_ICON_TINT,
  NOTICE_SURFACE,
} from "./notice-variants";

const alertVariants = cva(
  "relative w-full rounded-card border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4",
  {
    variants: {
      variant: {
        default: `${NOTICE_SURFACE.default} ${ALERT_ICON_TINT.default}`,
        destructive: `${NOTICE_SURFACE.destructive} ${ALERT_ICON_TINT.destructive}`,
        success: `${NOTICE_SURFACE.success} ${ALERT_ICON_TINT.success}`,
        warning: `${NOTICE_SURFACE.warning} ${ALERT_ICON_TINT.warning}`,
        info: `${NOTICE_SURFACE.info} ${ALERT_ICON_TINT.info}`,
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const Alert = React.forwardRef(({ className, variant = "default", ...props }, ref) => {
  const isDestructive = variant === "destructive" || variant === "warning";
  return (
    <div
      ref={ref}
      role={isDestructive ? "alert" : "status"}
      aria-live={isDestructive ? "assertive" : "polite"}
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
});
Alert.displayName = "Alert";

const AlertTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn("mb-1 font-display font-medium leading-none tracking-tight", className)}
    {...props}
  />
));
AlertTitle.displayName = "AlertTitle";

const AlertDescription = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm [&_p]:leading-relaxed", className)}
    {...props}
  />
));
AlertDescription.displayName = "AlertDescription";

const AlertIcon = ({ variant = "default" }) => {
  const icons = {
    default: Info,
    destructive: AlertCircle,
    success: CheckCircle2,
    warning: AlertTriangle,
    info: Info,
  };
  const Icon = icons[variant] || Info;
  return <Icon className="h-4 w-4" aria-hidden="true" />;
};

export { Alert, AlertTitle, AlertDescription, AlertIcon };
